import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const BUDGETS = ["$20k-$30k", "$30k-$40k", "$40k-$50k", "$50k-$75k", "$75k-$100k", "$100k+", "Not yet determined"];
const PROJECT_TYPES = ["Existing home", "Renovation", "New build", "Apartment", "Penthouse"];
const STAGES = ["exploring", "planning", "plans_underway", "builder_appointed", "ready_to_proceed"];
const STAGE_LABELS = { exploring: "Exploring", planning: "Planning", plans_underway: "Plans underway", builder_appointed: "Builder appointed", ready_to_proceed: "Ready to proceed" };
const TV_SIZES = ["65\"", "75\"", "85\"", "98\"", "100\"+"];
const FIREPLACES = ["None", "Electric", "Gas", "Bioethanol", "Not sure"];
const STYLES = ["Coastal", "Coastal Hampton", "Contemporary", "Organic Modern", "Minimalist", "Dark Luxury", "Stone Feature", "Timber Feature"];
const MATERIALS = ["Stone", "Marble", "Travertine", "Oak", "Walnut", "Blackbutt", "Microcement", "Fluted panels", "Plaster"];

const STEPS = [
  { key: "you", label: "About You" },
  { key: "property", label: "Property" },
  { key: "project", label: "Project" },
  { key: "stage", label: "Stage" },
  { key: "budget", label: "Investment" },
  { key: "design", label: "Design" },
  { key: "uploads", label: "Uploads" },
  { key: "pros", label: "Professionals" },
  { key: "timing", label: "Timing" },
];

export default function EnquiryPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inspirationId = params.get("inspiration");
  const professionalFlag = params.get("professional");
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(null);       // { lead_id, upload_token, reference }
  const [uploads, setUploads] = useState([]);
  const [inspirationDesign, setInspirationDesign] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", suburb: "", state: "QLD", postcode: "",
    project_type: "", project_stage: "", budget_band: "",
    tv_size: "", fireplace: "", audio: "", storage_requirement: "",
    material_preferences: [], style_preferences: [],
    inspiration_design_id: inspirationId || null,
    architect_involved: false, architect_name: "",
    builder_involved: false, builder_name: "",
    interior_designer_involved: !!professionalFlag, interior_designer_name: "",
    target_timing: "", comments: "",
    source: professionalFlag ? "professional" : "website",
  });

  useEffect(() => {
    if (inspirationId) {
      publicApi.get(`/public/designs`).then((r) => {
        const found = r.data.find((d) => d.id === inspirationId);
        if (found) setInspirationDesign(found);
      });
    }
  }, [inspirationId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleArr = (key, v) => set({ [key]: form[key].includes(v) ? form[key].filter((x) => x !== v) : [...form[key], v] });

  // Submit at step 6 (before uploads). Then uploads are attached to the created lead.
  const [submitting, setSubmitting] = useState(false);
  const submitCore = async () => {
    setSubmitting(true);
    try {
      const { data } = await publicApi.post("/public/enquiry", form);
      setSaved(data);
      setStep(6); // uploads
    } catch (e) {
      toast.error(formatErr(e) || "Could not send enquiry");
    } finally { setSubmitting(false); }
  };

  const finish = async () => {
    if (!saved) {
      await submitCore();
      return;
    }
    // Persist steps 8-9 (professionals/timing/comments) that were collected after the initial submit
    try {
      await publicApi.patch(`/public/enquiry/${saved.lead_id}`, {
        upload_token: saved.upload_token,
        architect_involved: form.architect_involved,
        architect_name: form.architect_name,
        interior_designer_involved: form.interior_designer_involved,
        interior_designer_name: form.interior_designer_name,
        builder_involved: form.builder_involved,
        builder_name: form.builder_name,
        target_timing: form.target_timing,
        comments: form.comments,
      });
      await publicApi.post(`/public/enquiry/${saved.lead_id}/finalise`, { upload_token: saved.upload_token });
    } catch (e) {
      toast.error("Could not save the final details, please try again");
      return;
    }
    toast.success("Enquiry received — we'll be in touch shortly.");
    setStep(STEPS.length);
  };

  const uploadFile = async (file, kind) => {
    if (!saved) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try {
      await publicApi.post(`/public/enquiry/${saved.lead_id}/files`, fd, {
        headers: { "X-Upload-Token": saved.upload_token, "Content-Type": "multipart/form-data" },
      });
      setUploads((u) => [...u, { name: file.name, kind }]);
      toast.success("Uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  // Step 6 is 'design'; index 6 == uploads once submitted (we always allow next up to submit at index 5)
  const currentStep = STEPS[step] || { key: "done" };
  const isFirst = step === 0;

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))]" data-testid="enquiry-page">
      <PublicNav/>
      <div className="pt-32 pb-16 max-w-3xl mx-auto px-6">
        <div className="overline">Request Design Consultation</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 52px)", lineHeight: 1.05 }}>
          Tell us about your project.
        </h1>
        <p className="text-muted-foreground mt-4">A few short steps. We'll respond within one business day with the next step in the process.</p>

        {inspirationDesign && (
          <div className="mt-6 luxe-card p-4 flex items-center gap-4">
            <div className="w-16 h-16 bg-muted overflow-hidden">
              <img src={inspirationDesign.hero_unsplash} className="w-full h-full object-cover" alt=""/>
            </div>
            <div className="flex-1">
              <div className="overline text-[10px]">Your saved inspiration</div>
              <div className="font-serif text-lg">{inspirationDesign.title}</div>
            </div>
          </div>
        )}

        {saved && (
          <div className="mt-6 border-l-2 border-[color:hsl(var(--accent))] bg-[color:hsl(var(--surface-panel))] p-4 text-sm">
            <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Reference</div>
            <div className="font-serif text-xl mt-1">{saved.reference}</div>
          </div>
        )}

        <div className="mt-10">
          {currentStep.key !== "done" && (
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full border text-[10px] flex items-center justify-center ${i < step ? "bg-foreground border-foreground text-background" : i === step ? "border-foreground" : "border-input text-muted-foreground"}`}>
                    {i < step ? <Check className="w-3 h-3" strokeWidth={2}/> : (i + 1)}
                  </div>
                  {i < STEPS.length - 1 && <div className="w-4 h-px bg-border"/>}
                </div>
              ))}
            </div>
          )}

          <div className="luxe-card p-8">
            {currentStep.key === "you" && (
              <StepAboutYou form={form} set={set}/>
            )}
            {currentStep.key === "property" && (
              <StepProperty form={form} set={set}/>
            )}
            {currentStep.key === "project" && (
              <StepProject form={form} set={set}/>
            )}
            {currentStep.key === "stage" && (
              <StepStage form={form} set={set}/>
            )}
            {currentStep.key === "budget" && (
              <StepBudget form={form} set={set}/>
            )}
            {currentStep.key === "design" && (
              <StepDesign form={form} set={set} toggleArr={toggleArr}/>
            )}
            {currentStep.key === "uploads" && (
              <StepUploads uploads={uploads} onUpload={uploadFile} saved={saved}/>
            )}
            {currentStep.key === "pros" && (
              <StepPros form={form} set={set}/>
            )}
            {currentStep.key === "timing" && (
              <StepTiming form={form} set={set}/>
            )}
            {currentStep.key === "done" && (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full mx-auto bg-[color:hsl(var(--accent))] text-white flex items-center justify-center">
                  <Check strokeWidth={2}/>
                </div>
                <div className="font-serif text-3xl mt-6">Thank you.</div>
                <p className="text-muted-foreground mt-3 max-w-md mx-auto">Your enquiry <strong>{saved?.reference}</strong> has been received. A member of our studio will be in touch within one business day.</p>
                <button data-testid="btn-enquiry-back-home" onClick={() => nav("/")} className="btn-outline mt-6 mx-auto">Return home</button>
              </div>
            )}
          </div>

          {currentStep.key !== "done" && (
            <div className="flex justify-between items-center mt-6">
              <button data-testid="btn-enquiry-prev" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={isFirst} className="btn-outline">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
              </button>
              {step < 5 && (
                <button data-testid="btn-enquiry-next" onClick={() => setStep(step + 1)} className="btn-primary" disabled={!canProceed(currentStep.key, form)}>
                  Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              )}
              {step === 5 && !saved && (
                <button data-testid="btn-enquiry-submit-partial" onClick={submitCore} className="btn-primary" disabled={submitting || !canProceed("design", form)}>
                  {submitting ? "Sending…" : "Continue"} <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              )}
              {step >= 6 && step < STEPS.length - 1 && (
                <button data-testid="btn-enquiry-next" onClick={() => setStep(step + 1)} className="btn-primary">
                  Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              )}
              {step === STEPS.length - 1 && (
                <button data-testid="btn-enquiry-finish" onClick={finish} className="btn-accent">
                  Request Design Consultation <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <PublicFooter/>
    </div>
  );
}

function canProceed(key, f) {
  if (key === "you") return f.first_name && f.last_name && f.email;
  if (key === "property") return f.suburb;
  if (key === "project") return f.project_type;
  if (key === "stage") return f.project_stage;
  if (key === "budget") return f.budget_band;
  if (key === "design") return true;
  return true;
}

function Field({ label, ...rest }) {
  return (
    <div>
      <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>{label}</label>
      <input className="luxe-input" {...rest}/>
    </div>
  );
}
function Choice({ options, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((o) => (
        <button key={o.value ?? o} data-testid={`${testid}-${o.value ?? o}`}
          onClick={() => onChange(o.value ?? o)}
          className={`text-left px-4 py-3 border ${value === (o.value ?? o) ? "border-foreground bg-foreground text-background" : "border-input hover:border-foreground"}`}>
          {o.label ?? o}
        </button>
      ))}
    </div>
  );
}

function StepAboutYou({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 1 · About You</div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="First name" data-testid="input-firstname" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })}/>
        <Field label="Last name" data-testid="input-lastname" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })}/>
      </div>
      <Field label="Email" type="email" data-testid="input-enq-email" value={form.email} onChange={(e) => set({ email: e.target.value })}/>
      <Field label="Phone (optional)" data-testid="input-enq-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })}/>
    </div>
  );
}
function StepProperty({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 2 · Property</div>
      <Field label="Street address (optional)" data-testid="input-enq-address" value={form.address} onChange={(e) => set({ address: e.target.value })}/>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Suburb" data-testid="input-enq-suburb" value={form.suburb} onChange={(e) => set({ suburb: e.target.value })}/>
        <Field label="State" data-testid="input-enq-state" value={form.state} onChange={(e) => set({ state: e.target.value })}/>
        <Field label="Postcode" data-testid="input-enq-postcode" value={form.postcode} onChange={(e) => set({ postcode: e.target.value })}/>
      </div>
    </div>
  );
}
function StepProject({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 3 · Project</div>
      <Choice options={PROJECT_TYPES} value={form.project_type} onChange={(v) => set({ project_type: v })} testid="proj-type"/>
    </div>
  );
}
function StepStage({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 4 · Stage</div>
      <Choice
        options={STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] }))}
        value={form.project_stage} onChange={(v) => set({ project_stage: v })} testid="stage"/>
    </div>
  );
}
function StepBudget({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 5 · Investment</div>
      <Choice options={BUDGETS} value={form.budget_band} onChange={(v) => set({ budget_band: v })} testid="budget"/>
    </div>
  );
}
function StepDesign({ form, set, toggleArr }) {
  return (
    <div className="space-y-6">
      <div className="overline">Step 6 · Design</div>
      <div>
        <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>TV size</label>
        <Choice options={TV_SIZES} value={form.tv_size} onChange={(v) => set({ tv_size: v })} testid="tv"/>
      </div>
      <div>
        <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Fireplace</label>
        <Choice options={FIREPLACES} value={form.fireplace} onChange={(v) => set({ fireplace: v })} testid="fire"/>
      </div>
      <div>
        <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Preferred styles</label>
        <div className="flex flex-wrap gap-2">
          {STYLES.map((s) => (
            <button key={s} data-testid={`chip-style-${s}`}
              onClick={() => toggleArr("style_preferences", s)}
              className={`px-3 py-2 text-xs border ${form.style_preferences.includes(s) ? "bg-foreground text-background border-foreground" : "border-input"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Preferred materials</label>
        <div className="flex flex-wrap gap-2">
          {MATERIALS.map((m) => (
            <button key={m} data-testid={`chip-material-${m}`}
              onClick={() => toggleArr("material_preferences", m)}
              className={`px-3 py-2 text-xs border ${form.material_preferences.includes(m) ? "bg-foreground text-background border-foreground" : "border-input"}`}>{m}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
function StepUploads({ uploads, onUpload, saved }) {
  const photoRef = useRef(); const planRef = useRef(); const inspRef = useRef();
  const item = (label, kind, ref) => (
    <div className="border border-dashed border-input p-6 text-center">
      <input type="file" ref={ref} accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], kind)}/>
      <div className="font-serif text-lg">{label}</div>
      <button data-testid={`btn-upload-${kind}`} onClick={() => ref.current?.click()} className="btn-outline mx-auto mt-3 text-xs">
        <Upload className="w-3 h-3" strokeWidth={1.5}/> Choose file
      </button>
      <div className="text-xs text-muted-foreground mt-2">
        {uploads.filter((u) => u.kind === kind).map((u) => u.name).join(", ") || "Optional"}
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="overline">Step 7 · Uploads</div>
      <p className="text-sm text-muted-foreground">Optional — but very helpful. Anything you share stays private.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {item("Room photos", "photo", photoRef)}
        {item("Floor plans", "plan", planRef)}
        {item("Inspiration", "inspiration", inspRef)}
      </div>
    </div>
  );
}
function StepPros({ form, set }) {
  const row = (key, name_key, label) => (
    <div className="p-4 border border-border">
      <label className="flex items-center gap-3">
        <input type="checkbox" data-testid={`chk-${key}`} checked={form[key]} onChange={(e) => set({ [key]: e.target.checked })}/>
        <span className="font-serif text-lg">{label}</span>
      </label>
      {form[key] && (
        <input data-testid={`input-${name_key}`} className="luxe-input mt-3" placeholder="Name / studio" value={form[name_key]} onChange={(e) => set({ [name_key]: e.target.value })}/>
      )}
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="overline">Step 8 · Professionals</div>
      <p className="text-sm text-muted-foreground">Tell us who else is involved.</p>
      {row("architect_involved", "architect_name", "Architect")}
      {row("interior_designer_involved", "interior_designer_name", "Interior Designer")}
      {row("builder_involved", "builder_name", "Builder")}
    </div>
  );
}
function StepTiming({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="overline">Step 9 · Timing</div>
      <Field label="Target timing (e.g. 'By December' or 'ASAP')" data-testid="input-timing" value={form.target_timing} onChange={(e) => set({ target_timing: e.target.value })}/>
      <div>
        <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Anything else you'd like to share?</label>
        <textarea data-testid="input-comments" rows="3" className="luxe-input" value={form.comments} onChange={(e) => set({ comments: e.target.value })}/>
      </div>
    </div>
  );
}
